import { useEffect, useRef } from 'react'

/**
 * Load the next page when a marker near the end of the list comes into view.
 *
 * The marker is placed a little way below the last row, so the next page starts
 * arriving before the person reaches the bottom and the list feels continuous
 * rather than stop-start.
 *
 * `onReach` is held in a ref rather than listed as a dependency. It is rebuilt
 * on every render — it closes over how many images are on screen — and using it
 * as a dependency would tear down and rebuild the observer each time, which in
 * a list that grows as you scroll means doing that constantly.
 */
export function useInfiniteScroll(
  enabled: boolean,
  onReach: () => void,
  /**
   * The element that actually scrolls, when it is not the page itself. The Miro
   * panel scrolls inside a box, and the margin below is measured against
   * whatever is given here — against the window, a margin means nothing to a
   * list scrolling inside a 300px column.
   */
  scroller?: { current: HTMLElement | null },
) {
  const marker = useRef<HTMLDivElement | null>(null)
  const latest = useRef(onReach)
  latest.current = onReach

  useEffect(() => {
    const element = marker.current
    if (!element || !enabled) return
    // Older browsers without IntersectionObserver still have the button.
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) latest.current()
      },
      { root: scroller?.current ?? null, rootMargin: '600px 0px' },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [enabled, scroller])

  return marker
}
